import React, { useState, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import PropTypes from 'prop-types';
import { Button, Modal } from 'app/components';
import { CloudArrowUp, Image, Minus, Plus, PencilSimple, Trash, X } from '@phosphor-icons/react';

import './style';
import getCroppedImg from './util';

const ImageUpload = (props) => {
  const { 
    setFormData, 
    formData,
    enableDelete,
    eventImagesRef,
    readOnly,
  } = props;

  const [uploadImage,setUploadImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCrop, setShowCrop] = useState(false);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const inputRef = useRef(null);


  useEffect(() => {
    if (formData?.full_image || eventImagesRef?.current?.full_image) {
      setUploadImage(eventImagesRef?.current?.full_image || formData?.full_image);
      setCrop(formData.crop?.crop);
      setZoom(formData.crop?.zoom);
    }
  }, [formData?.full_image]);

  const handleDelete = () => {
    if (eventImagesRef?.current?.full_image) {
      eventImagesRef.current.full_image = null;
      eventImagesRef.current.image = null;
    }

    setFormData({ ...formData,
      image: null,
      full_image: null, 
    });
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleCrop = () => {
    setShowCrop(!showCrop);
  };

  const handleFile = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadImage(reader.result);
        toggleCrop();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleDone = async () => {
    try {
      const croppedImage = await getCroppedImg(uploadImage, croppedAreaPixels);
      eventImagesRef.current = {
        full_image: uploadImage, 
        image: croppedImage,
      };
      setFormData({ 
        ...formData, 
        crop : {
          croppedAreaPixels: croppedAreaPixels,
          crop: crop,
          zoom: zoom,
        },
      });
      toggleCrop();
      resetCropSettings();
    } catch (e) {
      console.error(e);
      toggleCrop();
    }
  };

  const resetCropSettings = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };


  return (
    <>
      {(!formData?.full_image && !eventImagesRef?.current?.full_image) && (
        <div className={`dropzone cursor-pointer`}
          onClick={() => inputRef.current.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}>
          <div className="p-8 flex flex-col justify-center items-center gap-5 bg-[#FDFDFD] rounded-xl">
            <Image size={24} />
            <div className="flex flex-col items-center justify-center gap-4">
              <h1>Drag and drop or</h1>
              <input type="file"
                onChange={handleFileChange}
                hidden
                ref={inputRef}
                accept="image/*" />
              <Button type="tertiary"
                iconLeft={<CloudArrowUp />}>
                Upload an image
              </Button>
            </div>
          </div>
        </div>
      )}

      {(eventImagesRef?.current?.image || formData?.full_image) && (
        <>
          <div className={`displayzone`}>
            <img className="displayImage"
              src={eventImagesRef?.current?.image || formData?.full_image}
              alt="Event Image" />
          </div>
          <div className="flex gap-8">
            {!readOnly && (<div 
              className="p-3 flex justify-center items-center rounded-[36px] bg-[#F3F3F3] cursor-pointer"
              onClick={toggleCrop}
            >
              <PencilSimple size={24}/>
            </div>)}

            {(!readOnly && !enableDelete) && <>
              <input 
                type="file"
                onChange={handleFileChange}
                hidden
                ref={inputRef}
                accept="image/*" />
              <Button type="tertiary"
                iconLeft={<CloudArrowUp />}
                onClick={() => inputRef.current.click()}>
              </Button>
            </>}

            {(enableDelete === true && !readOnly) &&  
              <div 
                className="p-3 flex justify-center items-center rounded-[36px] bg-[#F3F3F3] cursor-pointer"
                onClick={toggleModal}
              >
                <Trash size={24}/>
              </div>
            }
            
          </div>
        </>
      )}

      {/* Hidden file input for new uploads */}
      <input type="file"
        onChange={handleFileChange}
        hidden
        ref={inputRef}
        accept="image/*" 
      />

      {showCrop && (
        <Modal size="regular"
          className="modal-edit"
          hasClose={window.innerWidth > 768}
          onClose={toggleCrop}>
          {window.innerWidth <= 768 ? (
            <div className="crop-mobile-header flex items-center w-full gap-3">
              <button
                type="button"
                className="crop-close-btn"
                onClick={toggleCrop}
              >
                <X size={18} />
              </button>
              <p className="flex-1 text-center font-semibold whitespace-nowrap">Edit your photo</p>
              <Button type="primary-black"
                size="small"
                className="crop-btn shrink-0"
                onClick={handleDone}>
                Done
              </Button>
            </div>
          ) : (
            <div className="flex justify-end items-center absolute right-6 top-6 w-[235px]">
            <p className="flex-1 font-semibold">Edit your photo</p>
            <Button type="primary-black"
              size="small"
              className="crop-btn"
              onClick={handleDone}>
              Done
            </Button>
          </div>
          )}
          <div className="w-full h-[195px] relative">
            <Cropper
              image={uploadImage}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="slider-container mt-5">
            <button 
              className="p-2 flex justify-center bg-grey-10 rounded-3xl" 
              onClick={() => setZoom(Math.max(1, zoom - 0.1))}>
              <Minus size={12}/>
            </button>
            <input
              className="slider"
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
            />
            <button  
              className="p-2 flex justify-center bg-grey-10 rounded-3xl"
              onClick={() => setZoom(zoom + 0.1)}>
              <Plus size={12}/>
            </button>
          </div>
        </Modal>
      )}

      {showModal && (
        <Modal size="regular"
          onClick={toggleModal}
          className="modal-delete">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col justify-center items-center gap-6">
              <img className="h-[154px] w-full"
                src={uploadImage}
                alt="Event Image" />
              <h1 className="heading-24 text-center">Delete this image from <br/>your listing?</h1>
            </div>
            <div className="flex flex-col gap-2 justify-center">
              <Button className="w-full"
                type="primary-black"
                onClick={handleDelete}>
                Delete
              </Button>
              <Button className="w-full"
                type="tertiary"
                onClick={toggleModal}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

ImageUpload.propTypes = {
  setFormData: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  enableDelete: PropTypes.bool,
};

ImageUpload.defaultProps = {
  setFormData: () => null,
  formData: {},
  enableDelete: true,
};

export default ImageUpload;
